/**
 * Header redesign behaviours:
 * - Mobile: sticky; fixed only while menu open
 * - Desktop: fixed, top offset follows announcement / timer / notice above
 * - Transparent over banner when first MainContent section has [allow-transparent-header]
 * - Text L2 panels: Lösungen finden / Marke / Kundenservice
 * - Image-grid L2 panels: matched by image_grid_card parent menu title
 * - Complex L2: models + Leistung destinations on desktop; L3 overlay on mobile only
 */
(function () {
  if (customElements.get('header-redesign')) return;

  const DESKTOP_MQ = '(min-width: 1024px)';

  class HeaderRedesign extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.menuOpen = false;
      this.panelOpen = false;
      this.l3Open = false;
      this.headerHovered = false;
      this.toggle = this.querySelector('[data-hr-menu-toggle]');
      this.drawer = this.querySelector('.header-redesign__drawer');
      this.l3 = this.querySelector('[data-hr-l3]');
      this.sectionEl = this.closest('.shopify-section');
      this.capable = this.dataset.transparentCapable === 'true';
      this.transparentEnabled = this.capable && this.hasTransparentBanner();
      this.desktopMq = window.matchMedia(DESKTOP_MQ);

      this.toggle?.addEventListener('click', () => this.setMenuOpen(!this.menuOpen));

      // Transparent header: solid white while pointer is over the whole header (not per-link)
      this.addEventListener('mouseenter', () => {
        if (!this.isDesktop() || !this.transparentEnabled) return;
        this.headerHovered = true;
        this.syncSolidState();
      });
      this.addEventListener('mouseleave', () => {
        this.headerHovered = false;
        this.syncSolidState();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.l3Open) {
          this.closeL3();
          return;
        }
        if (this.menuOpen) {
          this.setMenuOpen(false);
          this.toggle?.focus();
          return;
        }
        this.closeDesktopPanels();
      });
      this.addEventListener('click', (event) => {
        const option = event.target.closest('.header-redesign__locale-option');
        if (option) {
          event.preventDefault();
          const form = option.closest('form');
          const input = form?.querySelector('input[name="country_code"]');
          if (!form || !input) return;
          input.value = option.getAttribute('data-value') || input.value;
          form.submit();
          return;
        }

        const l3OpenBtn = event.target.closest('[data-hr-l3-open]');
        if (l3OpenBtn) {
          if (this.isDesktop()) {
            // Desktop complex stage: click follows filtered/collection URL.
            // Hover (see mouseover below) switches the right-hand product list.
            const href = l3OpenBtn.getAttribute('href') || '';
            if (!href || href === '#' || l3OpenBtn.getAttribute('aria-disabled') === 'true') {
              event.preventDefault();
              const complex = l3OpenBtn.closest('[data-hr-complex]');
              if (complex?.querySelector('[data-hr-complex-stage]')) {
                this.activateComplexCategory(complex, l3OpenBtn);
              }
            }
            return;
          }
          // Mobile: open L3 when match count > 1 (set in Liquid); otherwise follow href.
          if (l3OpenBtn.getAttribute('data-hr-mobile-l3') === 'true') {
            event.preventDefault();
            this.openL3({
              title: l3OpenBtn.getAttribute('data-hr-l3-title') || 'Produkte',
              key: l3OpenBtn.getAttribute('data-hr-l3-key') || '',
            });
          }
          return;
        }

        if (event.target.closest('[data-hr-l3-back]')) {
          event.preventDefault();
          this.closeL3();
          return;
        }

        const filterRoot = event.target.closest('[data-hr-l3-filters]');
        if (filterRoot) {
          const filterAll = event.target.closest('[data-hr-l3-filter="all"]');
          if (filterAll) {
            event.preventDefault();
            this.setL3Filter(filterRoot, 'all');
            return;
          }

          if (event.target.closest('[data-hr-l3-filter-toggle]')) {
            event.preventDefault();
            this.toggleL3Leistung(filterRoot);
            return;
          }

          const powerOption = event.target.closest('[data-hr-l3-filter="power"]');
          if (powerOption) {
            event.preventDefault();
            this.setL3Filter(filterRoot, 'power', powerOption.getAttribute('data-hr-l3-power') || '');
            return;
          }
        }

        const accordionToggle = event.target.closest('[data-hr-accordion-toggle]');
        if (accordionToggle) {
          event.preventDefault();
          this.toggleMobileAccordion(accordionToggle.closest('.header-redesign__drawer-item'));
        }
      });

      // Desktop complex mega: hover left category → update right product list
      this.addEventListener('mouseover', (event) => {
        if (!this.isDesktop()) return;
        const trigger = event.target.closest('.header-redesign__complex--desktop [data-hr-l3-open]');
        if (!trigger) return;
        const complex = trigger.closest('[data-hr-complex]');
        if (!complex?.querySelector('[data-hr-complex-stage]')) return;
        if (trigger.classList.contains('is-active')) return;
        this.activateComplexCategory(complex, trigger);
      });

      this.addEventListener('focusin', (event) => {
        if (!this.isDesktop()) return;
        const trigger = event.target.closest('.header-redesign__complex--desktop [data-hr-l3-open]');
        if (!trigger) return;
        const complex = trigger.closest('[data-hr-complex]');
        if (!complex?.querySelector('[data-hr-complex-stage]')) return;
        this.activateComplexCategory(complex, trigger);
      });

      this.querySelectorAll('.header-redesign__nav-item--panel').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (!this.isDesktop()) return;
          this.openDesktopPanel(item);
        });
        item.addEventListener('mouseleave', () => {
          if (!this.isDesktop()) return;
          this.closeDesktopPanel(item);
        });
        item.addEventListener('focusin', () => {
          if (!this.isDesktop()) return;
          this.openDesktopPanel(item);
        });
        item.addEventListener('focusout', (event) => {
          if (!this.isDesktop()) return;
          if (item.contains(event.relatedTarget)) return;
          this.closeDesktopPanel(item);
        });
      });

      this.onScroll = () => {
        this.syncOffsetTop();
        this.syncSolidState();
      };
      this.onResize = () => {
        this.setHeight();
        this.syncOffsetTop();
        this.syncSolidState();
        if (!this.isDesktop()) {
          this.closeDesktopPanels();
        } else if (this.l3Open) {
          this.closeL3();
        }
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.desktopMq.addEventListener?.('change', this.onResize);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this);
        if (this.sectionEl?.parentElement) {
          this.resizeObserver.observe(this.sectionEl.parentElement);
        }
        let sibling = this.sectionEl?.previousElementSibling;
        while (sibling) {
          if (sibling.matches?.('.shopify-section')) {
            this.resizeObserver.observe(sibling);
          }
          sibling = sibling.previousElementSibling;
        }
      }

      this.dataset.transparent = this.transparentEnabled ? 'true' : 'false';
      this.classList.toggle('header-redesign--overlay', this.transparentEnabled);
      document.body.classList.toggle('header-redesign-overlay-page', this.transparentEnabled);

      this.setHeight();
      this.syncOffsetTop();

      if (this.transparentEnabled) {
        this.syncSolidState();
      } else {
        this.classList.add('is-solid');
        this.classList.remove('is-transparent');
      }

    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      this.desktopMq?.removeEventListener?.('change', this.onResize);
      this.resizeObserver?.disconnect();
      document.body.classList.remove('header-redesign-lock');
      document.body.classList.remove('header-redesign-overlay-page');
    }

    isDesktop() {
      return this.desktopMq?.matches ?? window.matchMedia(DESKTOP_MQ).matches;
    }

    hasTransparentBanner() {
      return Boolean(
        document.querySelector('#MainContent > .shopify-section:first-child [allow-transparent-header]')
      );
    }

    syncOffsetTop() {
      if (!this.isDesktop()) {
        // Mobile sticky top:0 — promo bars above are in normal flow (like Bluetti).
        this.style.setProperty('--hr-offset-top', '0px');
        return;
      }

      const section = this.sectionEl;
      if (!section) {
        this.style.setProperty('--hr-offset-top', '0px');
        return;
      }

      // Desktop fixed header: top = header-section viewport top, which already
      // includes in-flow promo/announcement height and shrinks while scrolling.
      const top = Math.max(0, Math.round(section.getBoundingClientRect().top));
      this.style.setProperty('--hr-offset-top', `${top}px`);
    }

    setHeight() {
      const height = Math.round(this.querySelector('.header-redesign__bar')?.offsetHeight || this.offsetHeight || 0);
      if (height > 0) {
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    }

    setMenuOpen(open) {
      this.menuOpen = Boolean(open);
      this.classList.toggle('is-menu-open', this.menuOpen);
      this.toggle?.setAttribute('aria-expanded', this.menuOpen ? 'true' : 'false');
      if (this.drawer) {
        this.drawer.setAttribute('aria-hidden', this.menuOpen ? 'false' : 'true');
      }
      document.body.classList.toggle('header-redesign-lock', this.menuOpen || this.l3Open);
      if (!this.menuOpen) {
        this.closeL3();
        this.closeMobileAccordions();
      }
      this.syncSolidState();
      this.syncFloatingWidgets();
    }

    syncFloatingWidgets() {
      const hide = this.menuOpen || this.l3Open || this.panelOpen || this.classList.contains('is-panel-open');
      document.documentElement.toggleAttribute('data-hr-nav-open', hide);

      const nodes = document.querySelectorAll(
        '[id^="trustbadge-container-"], [id*="trustbadge-container"], [class*="kl-teaser-"]'
      );
      nodes.forEach((el) => {
        if (hide) {
          if (!el.hasAttribute('data-hr-prev-display')) {
            el.setAttribute('data-hr-prev-display', el.style.display || '');
          }
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.setAttribute('aria-hidden', 'true');
        } else if (el.hasAttribute('data-hr-prev-display')) {
          const prev = el.getAttribute('data-hr-prev-display') || '';
          el.style.removeProperty('display');
          el.style.removeProperty('visibility');
          el.style.removeProperty('pointer-events');
          if (prev) el.style.display = prev;
          el.removeAttribute('data-hr-prev-display');
          el.removeAttribute('aria-hidden');
        }
      });
    }

    openDesktopPanel(item) {
      if (!item || !this.isDesktop()) return;
      // Keep backdrop up while switching between L2 panels (no flash)
      this.classList.add('is-panel-open');
      this.closeDesktopPanels(item);
      const toggle = item.querySelector('[data-hr-panel-toggle]');
      const dropdown = item.querySelector('[data-hr-dropdown]');
      if (dropdown) {
        window.clearTimeout(item._hrCloseTimer);
        dropdown.hidden = false;
        void dropdown.offsetWidth;
      }
      item.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      this.panelOpen = true;
      this.syncSolidState();
      this.syncFloatingWidgets();

      const complex = item.querySelector('[data-hr-complex]');
      if (complex) {
        const active = complex.querySelector('[data-hr-l3-open].is-active');
        const fallback =
          active ||
          complex.querySelector(`[data-hr-l3-key="${complex.querySelector('[data-hr-complex-stage]')?.getAttribute('data-hr-default-key') || ''}"]`) ||
          complex.querySelector('[data-hr-l3-open]');
        if (fallback) this.activateComplexCategory(complex, fallback);
      }
    }

    activateComplexCategory(complex, trigger) {
      if (!complex || !trigger) return;
      const key = trigger.getAttribute('data-hr-l3-key') || '';
      const stage = complex.querySelector('[data-hr-complex-stage]');
      complex.querySelectorAll('[data-hr-l3-open]').forEach((el) => {
        el.classList.toggle('is-active', el === trigger);
      });
      if (stage) this.showL3Group(key, stage);
    }

    closeDesktopPanel(item, options = {}) {
      if (!item) return;
      const keepOverlay = Boolean(options.keepOverlay);
      item.classList.remove('is-open');
      item.querySelector('[data-hr-panel-toggle]')?.setAttribute('aria-expanded', 'false');
      const dropdown = item.querySelector('[data-hr-dropdown]');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hideDelay = reduceMotion ? 0 : 420;
      window.clearTimeout(item._hrCloseTimer);
      item._hrCloseTimer = window.setTimeout(() => {
        if (!item.classList.contains('is-open') && dropdown) dropdown.hidden = true;
      }, hideDelay);
      this.panelOpen = this.querySelector('.header-redesign__nav-item.is-open') != null;
      if (!keepOverlay) {
        this.classList.toggle('is-panel-open', this.panelOpen);
      }
      this.syncSolidState();
      this.syncFloatingWidgets();
    }

    closeDesktopPanels(except) {
      const switching = Boolean(except);
      this.querySelectorAll('.header-redesign__nav-item.is-open').forEach((item) => {
        if (item === except) return;
        this.closeDesktopPanel(item, { keepOverlay: switching });
      });
      this.panelOpen = this.querySelector('.header-redesign__nav-item.is-open') != null;
      if (!switching) {
        this.classList.toggle('is-panel-open', this.panelOpen);
      }
      this.syncSolidState();
      this.syncFloatingWidgets();
    }

    toggleMobileAccordion(item) {
      if (!item) return;
      const willOpen = !item.classList.contains('is-open');
      const toggle = item.querySelector('[data-hr-accordion-toggle]');
      const panel = item.querySelector('[data-hr-accordion]');

      if (willOpen) {
        item.classList.add('is-open');
        toggle?.setAttribute('aria-expanded', 'true');
        panel?.setAttribute('aria-hidden', 'false');
      } else {
        item.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        panel?.setAttribute('aria-hidden', 'true');
      }
    }

    closeMobileAccordions() {
      this.querySelectorAll('.header-redesign__drawer-item.is-open').forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('[data-hr-accordion-toggle]')?.setAttribute('aria-expanded', 'false');
        item.querySelector('[data-hr-accordion]')?.setAttribute('aria-hidden', 'true');
      });
    }

    openL3({ title, key }) {
      if (!this.l3 || this.isDesktop()) return;
      const titleEl = this.l3.querySelector('[data-hr-l3-title]');
      if (titleEl) titleEl.textContent = title || 'Produkte';
      this.showL3Group(key, this.l3);
      const filters = this.l3.querySelector('[data-hr-l3-filters]');
      if (filters) this.setL3Filter(filters, 'all');
      this.l3.hidden = false;
      this.l3.setAttribute('aria-hidden', 'false');
      void this.l3.offsetWidth;
      this.classList.add('is-l3-open');
      this.l3Open = true;
      document.body.classList.add('header-redesign-lock');
      this.l3.querySelector('[data-hr-l3-back]')?.focus();
      this.syncSolidState();
      this.syncFloatingWidgets();
    }

    closeL3() {
      if (!this.l3 || !this.l3Open) return;
      this.classList.remove('is-l3-open');
      const filters = this.l3.querySelector('[data-hr-l3-filters]');
      if (filters) this.closeL3Leistung(filters);
      this.l3Open = false;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hideDelay = reduceMotion ? 0 : 280;
      window.clearTimeout(this._hrL3CloseTimer);
      this._hrL3CloseTimer = window.setTimeout(() => {
        if (!this.l3Open && this.l3) {
          this.l3.hidden = true;
          this.l3.setAttribute('aria-hidden', 'true');
        }
      }, hideDelay);
      if (!this.menuOpen) document.body.classList.remove('header-redesign-lock');
      this.syncSolidState();
      this.syncFloatingWidgets();
    }

    showL3Group(key, root) {
      const scope = root || this;
      const groups = scope.querySelectorAll('[data-hr-l3-group]');
      if (!groups.length) return;

      let matched = null;
      groups.forEach((group) => {
        const groupKey = group.getAttribute('data-hr-l3-group') || '';
        const isMatch = Boolean(key) && groupKey === key;
        group.hidden = !isMatch;
        if (isMatch) matched = group;
      });

      if (!matched) {
        const empty = scope.querySelector('[data-hr-l3-group="__empty"]');
        const fallback = empty || groups[0];
        groups.forEach((group) => {
          group.hidden = group !== fallback;
        });
      }
    }

    setL3Filter(filterRoot, mode, powerLabel) {
      if (!filterRoot) return;
      const allBtn = filterRoot.querySelector('[data-hr-l3-filter="all"]');
      allBtn?.classList.toggle('is-active', mode === 'all');
      allBtn?.setAttribute('aria-pressed', mode === 'all' ? 'true' : 'false');

      const filterToggle = filterRoot.querySelector('[data-hr-l3-filter-toggle]');
      if (filterToggle) {
        const label = filterToggle.querySelector('span');
        if (mode === 'power' && powerLabel) {
          filterToggle.classList.add('is-active');
          if (label) label.textContent = powerLabel;
        } else {
          filterToggle.classList.remove('is-active');
          if (label) label.textContent = 'Leistung';
        }
      }

      this.closeL3Leistung(filterRoot);
      // Real product filtering deferred — shell only for now
    }

    toggleL3Leistung(filterRoot) {
      const leistung = filterRoot?.querySelector('[data-hr-l3-leistung]');
      const toggle = filterRoot?.querySelector('[data-hr-l3-filter-toggle]');
      if (!leistung) return;
      const willOpen = leistung.hidden;
      leistung.hidden = !willOpen;
      toggle?.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      toggle?.classList.toggle('is-open', willOpen);
    }

    closeL3Leistung(filterRoot) {
      const leistung = filterRoot?.querySelector('[data-hr-l3-leistung]');
      const toggle = filterRoot?.querySelector('[data-hr-l3-filter-toggle]');
      if (!leistung) return;
      leistung.hidden = true;
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.classList.remove('is-open');
    }

    syncSolidState() {
      if (!this.transparentEnabled) {
        this.classList.add('is-solid');
        this.classList.remove('is-transparent');
        return;
      }

      let scrolled;
      if (this.isDesktop()) {
        const offset = parseFloat(getComputedStyle(this).getPropertyValue('--hr-offset-top')) || 0;
        scrolled = offset <= 0 && (window.scrollY || 0) > 12;
      } else {
        scrolled = (window.scrollY || 0) > 12;
      }

      const panelActive = this.panelOpen || this.classList.contains('is-panel-open');
      const solid =
        scrolled || this.menuOpen || this.headerHovered || panelActive || this.l3Open;
      this.classList.toggle('is-solid', solid);
      this.classList.toggle('is-transparent', !solid);
    }
  }

  customElements.define('header-redesign', HeaderRedesign);
})();
